import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Building2, Check, Cpu, Users } from 'lucide-react';
import { formatCurrency, formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useApi } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useCurrency } from '@/Hooks/useCurrency';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/states';
import { apiMessage } from '../helpers';
import type { LimitedResource } from '../planLimits';
import PlanRequestDialog from './PlanRequestDialog';

/**
 * The tenant's own plan: what it costs, what it allows, how much of that is spent, and the way
 * off it.
 *
 * WHAT CHANGED HERE AND WHY IT MATTERS. This panel used to describe its own ceilings as
 * ADVISORY, in two strings written for a truthful reason: `assertWithinPlanLimits` existed and
 * nothing called it, so nothing was blocked and promising a refusal would have sent operators
 * hunting for a plan change to fix a create that was never going to fail. That is no longer
 * true — `users.service`, `branches.service` and `devices.service` all call the guard, a venue
 * on STARTER genuinely cannot open a second branch — so the two advisory strings go back to
 * stating a refusal, exactly as the old comment said they should.
 *
 * The usage numbers no longer come from three counting requests either. They used to, because
 * `GET /subscriptions/tenants/:id/usage` is gated on `platform.view`, which a tenant admin
 * does not hold — so this screen counted `/branches`, `/users` and `/devices` itself and
 * showed "not visible to you" for whichever of the three the actor could not read. There is
 * now `GET /subscriptions/current/usage` on `settings.view`, which takes no tenant id at all
 * and so cannot be pointed at anybody else. One request, no permission-shaped holes in the
 * answer, and the counting logic that could disagree with the server's is gone.
 *
 * NO PAYMENT ANYWHERE. Requesting is a message to the platform operator, and approval is what
 * moves the subscription. There is no gateway in this product and this screen must never
 * suggest there is one — no card fields, no "pay now", no total.
 */

interface Plan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  /** A decimal STRING. Never `Number()` it — `formatCurrency` takes the string as given. */
  priceMonthly: string;
  currency: string;
  maxBranches: number | null;
  maxUsers: number | null;
  maxDevices: number | null;
  features: Record<string, unknown> | null;
}

interface CurrentSubscription {
  id: string;
  status: string;
  startsAt: string;
  endsAt: string | null;
  trialEndsAt: string | null;
  plan: Plan;
}

interface Usage {
  branches: { used: number; limit: number | null };
  users: { used: number; limit: number | null };
  devices: { used: number; limit: number | null };
}

type RequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

interface PlanChangeRequest {
  id: string;
  planId: string;
  status: RequestStatus;
  note: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  plan: Pick<Plan, 'id' | 'code' | 'name' | 'priceMonthly' | 'currency'>;
}

const ICONS: Record<LimitedResource, typeof Building2> = {
  branches: Building2,
  users: Users,
  devices: Cpu,
};

/**
 * How full a meter has to be before it is called out. Not a hard number the API knows about —
 * the API only knows full or not — but "one branch left of five" is the moment a venue can
 * still plan around, and the moment after it is a refused create in the middle of a shift.
 */
const NEAR_CEILING = 0.8;

export default function PlanPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { confirm } = useConfirm();
  const { code: fallbackCurrency } = useCurrency();
  const [requesting, setRequesting] = useState<Plan | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const subscriptionQuery = useApi<CurrentSubscription>('/subscriptions/current');
  const usageQuery = useApi<{ usage: Usage }>('/subscriptions/current/usage');
  const plansQuery = useApi<Plan[]>('/subscriptions/current/plans');
  const requestsQuery = useApi<PlanChangeRequest[]>('/plan-change-requests/mine');

  const subscription = subscriptionQuery.data;
  const plan = subscription?.plan ?? null;
  const usage = usageQuery.data?.usage ?? null;
  const plans = plansQuery.data ?? [];
  const requests = requestsQuery.data ?? [];

  const mayRequest = can('subscription.request_change');
  const open = requests.find((request) => request.status === 'PENDING') ?? null;
  // The most recent ANSWER, so a rejection and its reason survive on the screen rather than
  // living only in a notification the operator may never have opened.
  const lastDecision = requests.find(
    (request) => request.status === 'APPROVED' || request.status === 'REJECTED',
  );

  /**
   * Is `candidate` a step UP from the plan this venue is on?
   *
   * Answered by POSITION, not by arithmetic. The API returns the plans ordered by
   * `priceMonthly` ascending, so "after the current one in this list" is the same question as
   * "costs more" — and it is answerable without turning two decimal strings into floats, which
   * is the one thing money must never do in this codebase. A current plan that has since been
   * retired is not in the list at all; the answer is then "unknown", and the button says
   * "request this plan" rather than guessing at a direction.
   */
  const isUpgrade = (candidate: Plan): boolean | null => {
    if (!plan) return null;
    const here = plans.findIndex((p) => p.id === plan.id);
    const there = plans.findIndex((p) => p.id === candidate.id);
    if (here < 0 || there < 0) return null;
    return there > here;
  };

  const priceOf = (p: Pick<Plan, 'priceMonthly' | 'currency'>) =>
    formatCurrency(p.priceMonthly, p.currency || fallbackCurrency, i18n.language);

  const reloadRequests = () => requestsQuery.reload();

  const cancelRequest = async (id: string) => {
    const confirmed = await confirm({
      title: t('settings.plan.cancelConfirmTitle'),
      body: t('settings.plan.cancelConfirmBody'),
      confirmLabel: t('settings.plan.cancelRequest'),
      tone: 'danger',
    });
    if (!confirmed) return;

    setCancelling(true);
    try {
      await api.post(`/plan-change-requests/mine/${id}/cancel`);
      toast.success(t('settings.plan.cancelled'));
      reloadRequests();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    } finally {
      setCancelling(false);
    }
  };

  if (subscriptionQuery.loading) return <LoadingState label={t('common.loading')} />;
  // A tenant with no live subscription is a real state, not an error: the API answers 404 and
  // the honest thing to say is that no plan is running.
  if (subscriptionQuery.error || !subscription || !plan) {
    return <EmptyState title={t('settings.plan.none')} hint={t('settings.plan.noneHint')} />;
  }

  const features = Object.entries(plan.features ?? {}).filter(([, value]) => value !== false);

  const meters: Array<{ key: LimitedResource; used: number | null; limit: number | null }> = [
    { key: 'branches', used: usage?.branches.used ?? null, limit: usage?.branches.limit ?? null },
    { key: 'users', used: usage?.users.used ?? null, limit: usage?.users.limit ?? null },
    { key: 'devices', used: usage?.devices.used ?? null, limit: usage?.devices.limit ?? null },
  ];

  return (
    <div className="space-y-5">
      {/* ---------------------------------------------------------- current */}
      <section className="space-y-4 rounded-xl border p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">{plan.name}</h2>
            <p className="text-sm text-ink-muted">{plan.description || plan.code}</p>
          </div>
          <div className="text-end">
            <Badge tone={toneForStatus(subscription.status)}>
              {t(`settings.plan.status.${subscription.status}`)}
            </Badge>
            <p className="mt-1 text-sm">{t('settings.plan.perMonth', { price: priceOf(plan) })}</p>
          </div>
        </div>

        <dl className="grid gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs text-ink-muted">{t('settings.plan.startedAt')}</dt>
            <dd className="text-sm">{formatDateTime(subscription.startsAt, i18n.language)}</dd>
          </div>
          {subscription.trialEndsAt && (
            <div>
              <dt className="text-xs text-ink-muted">{t('settings.plan.trialEndsAt')}</dt>
              <dd className="text-sm">{formatDateTime(subscription.trialEndsAt, i18n.language)}</dd>
            </div>
          )}
          <div>
            <dt className="text-xs text-ink-muted">{t('settings.plan.endsAt')}</dt>
            <dd className="text-sm">
              {subscription.endsAt
                ? formatDateTime(subscription.endsAt, i18n.language)
                : t('settings.plan.openEnded')}
            </dd>
          </div>
        </dl>

        {features.length > 0 && (
          <div className="space-y-2 border-t pt-4">
            <h3 className="text-sm font-semibold">{t('settings.plan.featuresTitle')}</h3>
            <ul className="flex flex-wrap gap-2">
              {features.map(([key, value]) => (
                <li key={key}>
                  <Badge tone="brand">
                    {/* The set is whatever `SubscriptionPlan.features` holds — the server
                        decides it, per plan, and it changes without a release here. A key with
                        no translation renders as itself rather than as a dotted path. */}
                    {i18n.exists(`settings.plan.feature.${key}`)
                      ? t(`settings.plan.feature.${key}`)
                      : key}
                    {typeof value !== 'boolean' && `: ${String(value)}`}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* ------------------------------------------------------------ usage */}
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">{t('settings.plan.usageTitle')}</h2>
          <p className="text-sm text-ink-muted">{t('settings.plan.usageHint')}</p>
        </div>

        {usageQuery.loading && !usage ? (
          <LoadingState label={t('common.loading')} />
        ) : !usage ? (
          /* An unread count is NOT a count of zero. Drawing empty meters here would tell a
             venue at its ceiling that it has room, which is the exact wrong answer to give
             on the screen that exists to explain a refusal. */
          <ErrorState
            message={usageQuery.error ?? t('settings.errors.loadFailed')}
            onRetry={usageQuery.reload}
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-3">
            {meters.map((meter) => (
              <UsageMeter
                key={meter.key}
                resource={meter.key}
                used={meter.used}
                limit={meter.limit}
              />
            ))}
          </div>
        )}
      </section>

      {/* ---------------------------------------------------- open request */}
      {open && (
        <section
          className="space-y-2 rounded-xl border border-sky-300 bg-sky-50 p-4 dark:border-sky-800 dark:bg-sky-950/40"
          aria-live="polite"
        >
          <h2 className="text-sm font-semibold">{t('settings.plan.openTitle')}</h2>
          <p className="text-sm text-ink-muted">
            {t('settings.plan.openBody', {
              plan: open.plan.name,
              date: formatDateTime(open.createdAt, i18n.language),
            })}
          </p>
          {open.note && (
            <p className="text-sm text-ink-muted">
              {t('settings.plan.yourNote', { note: open.note })}
            </p>
          )}
          {mayRequest && (
            <Button
              variant="outline"
              size="sm"
              disabled={cancelling}
              onClick={() => void cancelRequest(open.id)}
            >
              {t('settings.plan.cancelRequest')}
            </Button>
          )}
        </section>
      )}

      {/* ------------------------------------------------------- last answer */}
      {!open && lastDecision && (
        <section className="space-y-1 rounded-xl border p-4">
          <h2 className="text-sm font-semibold">
            {lastDecision.status === 'APPROVED'
              ? t('settings.plan.decisionApproved', { plan: lastDecision.plan.name })
              : t('settings.plan.decisionRejected', { plan: lastDecision.plan.name })}
          </h2>
          {lastDecision.decidedAt && (
            <p className="text-xs text-ink-muted">
              {formatDateTime(lastDecision.decidedAt, i18n.language)}
            </p>
          )}
          {lastDecision.decisionNote && (
            <p className="text-sm text-ink-muted">
              {t('settings.plan.decisionNote', { note: lastDecision.decisionNote })}
            </p>
          )}
        </section>
      )}

      {/* ------------------------------------------------------------ plans */}
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">{t('settings.plan.plansTitle')}</h2>
          <p className="text-sm text-ink-muted">
            {mayRequest ? t('settings.plan.plansHint') : t('settings.plan.plansHintReadOnly')}
          </p>
        </div>

        {plansQuery.loading && plans.length === 0 ? (
          <LoadingState label={t('common.loading')} />
        ) : plans.length === 0 ? (
          <EmptyState title={t('settings.plan.plansEmpty')} />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {plans.map((option) => {
              const current = option.id === plan.id;
              const upgrade = isUpgrade(option);
              return (
                <article
                  key={option.id}
                  className={
                    current
                      ? 'space-y-3 rounded-xl border-2 border-accent-solid p-4'
                      : 'space-y-3 rounded-xl border p-4'
                  }
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold">{option.name}</h3>
                      <p className="text-sm text-ink-muted">
                        {t('settings.plan.perMonth', { price: priceOf(option) })}
                      </p>
                    </div>
                    {current && <Badge tone="brand">{t('settings.plan.currentPlan')}</Badge>}
                  </div>

                  {option.description && (
                    <p className="text-sm text-ink-muted">{option.description}</p>
                  )}

                  <ul className="space-y-1 text-sm">
                    {(
                      [
                        ['branches', option.maxBranches],
                        ['users', option.maxUsers],
                        ['devices', option.maxDevices],
                      ] as Array<[LimitedResource, number | null]>
                    ).map(([resource, limit]) => (
                      <li key={resource} className="flex items-center gap-2">
                        <Check className="h-4 w-4 shrink-0 text-ink-muted" />
                        <span>
                          {limit === null
                            ? t(`settings.plan.unlocks.${resource}Unlimited`)
                            : t(`settings.plan.unlocks.${resource}`, { limit })}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {!current && mayRequest && (
                    <Button
                      size="sm"
                      className="w-full"
                      disabled={Boolean(open)}
                      title={open ? t('settings.plan.oneAtATime') : undefined}
                      onClick={() => setRequesting(option)}
                    >
                      {upgrade === true
                        ? t('settings.plan.requestUpgrade')
                        : t('settings.plan.requestPlan')}
                    </Button>
                  )}
                </article>
              );
            })}
          </div>
        )}

        {Boolean(open) && mayRequest && (
          <p className="text-xs text-ink-muted">{t('settings.plan.oneAtATime')}</p>
        )}
      </section>

      <PlanRequestDialog
        plan={requesting}
        currentPlanName={plan.name}
        onClose={() => setRequesting(null)}
        onSent={() => {
          setRequesting(null);
          reloadRequests();
        }}
      />
    </div>
  );
}

/**
 * One ceiling, and how close this venue is to it.
 *
 * Three states worth telling apart, because they mean different things to whoever is reading:
 * FULL is "your next create is refused", NEAR is "you can still plan around this", and the
 * rest is fine. Colour alone would not carry that — a red bar is invisible to a reader who
 * cannot see red and silent to a screen reader — so each state also says what it is in words,
 * and the bar carries its numbers on the `progressbar` role.
 */
function UsageMeter({
  resource,
  used,
  limit,
}: {
  resource: LimitedResource;
  used: number | null;
  limit: number | null;
}) {
  const { t } = useTranslation();
  const Icon = ICONS[resource];
  const label = t(`settings.plan.resource.${resource}`);

  const unlimited = limit === null;
  const count = used ?? 0;
  const ceiling = limit ?? 0;
  const full = !unlimited && count >= ceiling;
  const ratio = unlimited || ceiling === 0 ? 0 : Math.min(1, count / ceiling);
  const near = !full && !unlimited && ratio >= NEAR_CEILING;

  const bar = full ? 'bg-red-500' : near ? 'bg-amber-500' : 'bg-accent-solid';

  return (
    <div className="space-y-2 rounded-xl border p-4">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Icon className="h-4 w-4 text-ink-muted" />
        {label}
      </div>

      <div className="text-2xl font-semibold">
        {count}
        <span className="ms-1 text-sm font-normal text-ink-muted">
          {unlimited ? t('settings.plan.unlimited') : t('settings.plan.ofLimit', { limit })}
        </span>
      </div>

      {!unlimited && (
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-subtle"
          role="progressbar"
          aria-valuenow={count}
          aria-valuemin={0}
          aria-valuemax={ceiling}
          aria-label={label}
        >
          <div className={`h-full ${bar}`} style={{ inlineSize: `${Math.round(ratio * 100)}%` }} />
        </div>
      )}

      {full && <p className="text-xs font-medium text-red-700 dark:text-red-400">{t('settings.plan.atCeiling')}</p>}
      {near && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {t('settings.plan.nearCeiling', { remaining: ceiling - count })}
        </p>
      )}
    </div>
  );
}
